import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import styled from 'styled-components';
import { GYPSUM } from 'HubStyleTokens/colors';
import { fetchUpgradeConfig } from 'pricing-pages-lib/api/fetchUpgradeConfig';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { getQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import { PROFESSIONAL } from 'self-service-api/constants/ProductLevels';
import ProductLineMap from 'self-service-api/constants/ProductLineMap';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import { getHighlightsForProductLine, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import ErrorPage from 'pricing-pages-ui/components/error-page/ErrorPage';
import { SCREENS } from '../../constants/tracking/Locations';
import { useIsEBX642Variant } from '../../hooks/ebx642Hooks';
import { useAnchorLinkScroll } from '../../hooks/htmlElementHooks';
import { useBusinessObjectives } from '../../hooks/useBusinessObjectives';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useFeatureStore } from '../../hooks/useFeatureStore';
import { useTrackRetail } from '../../hooks/useTrackRetail';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { highestProductLevelFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import ProductInfoContainer, { mainBreakpoints } from '../common/ProductInfoContainer';
import CompareProFeatureTable from '../feature-table/CompareProFeatureTable';
import ComparePlansLink from '../suite-starter-page/ComparePlansLink';
import CompareProInterstitialOverview from './CompareProInterstitialOverview';
const Background = styled.div.withConfig({
  displayName: "CompareProInterstitial__Background",
  componentId: "sc-11f7h2p-0"
})(["width:100%;z-index:-1;background-color:", ";"], GYPSUM);
const ComparePlansLinkContainer = styled.div.withConfig({
  displayName: "CompareProInterstitial__ComparePlansLinkContainer",
  componentId: "sc-11f7h2p-1"
})(["padding-top:40px;padding-bottom:40px;"]);
const ContentContainer = styled.div.withConfig({
  displayName: "CompareProInterstitial__ContentContainer",
  componentId: "sc-11f7h2p-2"
})(["", ""], mainBreakpoints);

const CompareProHeader = ({
  upgradeProduct,
  productLevel,
  productLine
}) => {
  return /*#__PURE__*/_jsxs(Background, {
    children: [/*#__PURE__*/_jsx(ContentContainer, {
      className: "m-x-auto",
      children: /*#__PURE__*/_jsx(CompareProInterstitialOverview, {
        upgradeProduct: upgradeProduct,
        productLevel: productLevel,
        productLine: productLine
      })
    }), /*#__PURE__*/_jsx(ComparePlansLinkContainer, {
      children: /*#__PURE__*/_jsx(ComparePlansLink, {})
    })]
  });
};

const CompareProInterstitial = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  const isEBX642Variant = useIsEBX642Variant();
  const [upgradeConfig, setUpgradeConfig] = useState();
  const [productLine, setProductLine] = useState();
  const [upgradeProduct, setUpgradeProduct] = useState();
  const [filteredHighlightedProducts, setFilteredHighlightedProducts] = useState([]);
  useDocumentTitle(upgradeProduct || 'default'); //Fetch Upgrade Config and Upgrade Product

  useEffect(() => {
    const {
      upgradeSource
    } = getQueryParams();

    if (isEBX642Variant) {
      fetchUpgradeConfig(upgradeSource).then(upgradeConfigResponse => {
        setUpgradeConfig(upgradeConfigResponse);
        const upgradeProductFormatted = upgradeConfigResponse.upgradeProducts[0].split('-')[0];
        setUpgradeProduct(upgradeProductFormatted);
      }).catch(() => {
        setUpgradeConfig(null);
      });
    } else {
      setUpgradeConfig(null);
    }
  }, [isEBX642Variant]); // set the productLine based on the upgradeProduct

  useEffect(() => {
    if (upgradeProduct !== undefined) {
      let PRODUCT_LINE;
      Object.keys(ProductLineMap).forEach(key => {
        const item = ProductLineMap[key];

        if (item.upgradeProduct === upgradeProduct) {
          PRODUCT_LINE = item.upgradeProduct.toUpperCase();
        }
      });
      setProductLine(PRODUCT_LINE);
    }
  }, [upgradeConfig, upgradeProduct]); // determine highest owned level of productLine

  const productLevelOwned = useSelector(state => {
    const hasPricingAndPurchasingSucceeded = hasPricingAndPurchasingSucceededSelector(state);

    if (hasPricingAndPurchasingSucceeded) {
      return highestProductLevelFromProductInventorySelector(state, {
        productLine
      });
    }

    return null;
  }); // get ApiNames for highest owned level of productLine and professional level of productLine

  useEffect(() => {
    if (upgradeProduct !== undefined) {
      const FeaturesForProductLine = {
        ['marketing']: getHighlightsForProductLine(ProductLines.MARKETING),
        ['sales']: getHighlightsForProductLine(ProductLines.SALES)
      };
      const highlightedProducts = FeaturesForProductLine[upgradeProduct];
      const highlightedProductsFiltered = highlightedProducts.filter(apiName => {
        const localProductLevel = getProductLevelFromApiNameString(apiName);
        return [productLevelOwned, PROFESSIONAL].includes(localProductLevel);
      });
      setFilteredHighlightedProducts(highlightedProductsFiltered);
    }
  }, [productLevelOwned, upgradeProduct]);
  useBusinessObjectives({
    highlightedProducts: filteredHighlightedProducts
  });
  useFeatureStore({
    highlightedProducts: filteredHighlightedProducts,
    productLine: ProductLines.MARKETING
  });
  useFeatureStore({
    highlightedProducts: filteredHighlightedProducts,
    productLine: ProductLines.SALES
  });
  const productsByProductLine = {
    [productLine]: filteredHighlightedProducts
  };
  const isLoading = isEBX642Variant === undefined || upgradeConfig === undefined || productsByProductLine === undefined || productLevelOwned === undefined || productLine === undefined || upgradeProduct === undefined;
  useEffect(() => {
    if (!isLoading && isEBX642Variant) {
      getUsageTracker().track('pageView', {
        screen: SCREENS.COMPARE_PROFESSIONAL
      });
    }
  }, [isEBX642Variant, isLoading]);
  return isEBX642Variant === false ? /*#__PURE__*/_jsx(ErrorPage, {}) : /*#__PURE__*/_jsxs("div", {
    "data-selenium-id": "page-success",
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "COMPARE_PROFESSIONAL_PAGE"
    }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: isLoading,
      Content: CompareProHeader,
      heights: [705],
      widths: ['100%'],
      contentProps: {
        upgradeProduct,
        productLine,
        productLevel: productLevelOwned
      }
    }), /*#__PURE__*/_jsx(ProductInfoContainer, {
      style: {
        margin: 'auto'
      },
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isLoading,
        Content: CompareProFeatureTable,
        contentProps: {
          productsByProductLine
        }
      })
    })]
  });
};

export default CompareProInterstitial;